import { observable, action, computed } from 'mobx'
import { Model, type ModelProps } from '@/infra/model'
import { Id } from '@/infra/id'
import { once } from 'lodash-es'
import { collection } from '@/infra/collection'
import {
  ApprovedTransaction,
  type ApprovedTransactionProps,
} from './ApprovedTransaction'
import { toPrecision } from '@/utils/number-util'

export interface DraftPayoutProps extends ModelProps<DraftPayout> {
  approvedTransactions?: Partial<ApprovedTransactionProps>[]
}

/**
 * A Payout that is being drafted.
 */
export class DraftPayout extends Model<DraftPayout> {
  id = Id.none

  @observable accessor etag = ''
  @observable accessor totalAmount = 0

  /**
   * The approved transactions.
   */
  readonly approvedTransactions = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new ApprovedTransaction(this),
  })

  /**
   * The default instance.
   */
  static get none() {
    return lazyNone()
  }

  /**
   * The approved transactions as a set of IDs.
   */
  @computed
  get approvedTransactionIds() {
    return new Set(this.approvedTransactions.items.map((t) => t.id))
  }

  /**
   * Approves the transactions.
   * Called after having approved the transactions on the server.
   */
  @action.bound
  approveTransactions(
    transactions: Partial<ApprovedTransactionProps>[],
    draftEtag: string,
  ) {
    this.etag = draftEtag
    for (const props of transactions) {
      const transaction = this.approvedTransactions.create(props)
      this.totalAmount = toPrecision(this.totalAmount + transaction.amount)
    }
  }

  /**
   * Removes the transactions.
   * Called after having removed the transactions from the
   * payout on the server.
   */
  @action.bound
  removeTransactions(transactions: ApprovedTransaction[], draftEtag: string) {
    this.etag = draftEtag
    for (const transaction of transactions) {
      this.totalAmount = toPrecision(this.totalAmount - transaction.amount)
      this.approvedTransactions.remove(transaction)
    }
  }

  @action.bound
  override set({
    approvedTransactions,
    ...props
  }: Partial<DraftPayoutProps>): this {
    if (approvedTransactions) {
      this.approvedTransactions.reset(approvedTransactions)
    }

    return super.set(props)
  }
}

const lazyNone = once(() => new DraftPayout())
